import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { AUDIT_VIEWS, AuditView } from "../../models/AuditView";
import { AUDIT_TOKENS } from "../../theme/auditTokens";
import { visuallyHidden } from "../../theme/visuallyHidden";
import { formatNumber } from "@/utilities/formatNumber";

export const VIEW_TONES: Record<AuditView, { fg: string; bg: string }> = {
  digital: AUDIT_TOKENS.ok,
  analog: AUDIT_TOKENS.warn,
};

const MIN_GROW = 0.15;

const Segment = styled(ButtonBase, {
  shouldForwardProp: prop => prop !== "view" && prop !== "active",
})<{ view: AuditView; active: boolean }>(({ view, active }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 6,
  minWidth: 0,
  padding: "0 6px",
  fontFamily: AUDIT_TOKENS.fontFamily,
  fontSize: 12.5,
  fontWeight: 650,
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
  overflow: "hidden",
  background: VIEW_TONES[view].bg,
  color: VIEW_TONES[view].fg,
  boxShadow: active ? `inset 0 0 0 2px ${AUDIT_TOKENS.ink}` : "none",
  transition: "filter .15s, flex-grow .25s ease",
  "&:hover": { filter: "brightness(.96)" },
  "&.Mui-focusVisible": { outline: AUDIT_TOKENS.focusRing, outlineOffset: -3 },
  "@media (max-width: 820px)": {
    minHeight: 44,
    fontSize: 12.5,
    whiteSpace: "normal",
    lineHeight: 1.2,
    textAlign: "center",
    padding: "4px 6px",
  },
  "@media (prefers-reduced-motion: reduce)": { transition: "none" },
}));

interface Props {
  view: AuditView;
  onChange: (view: AuditView) => void;
  /** Undefined while the counts are unknown (load failed or not in yet): the segments then show a dash, not 0. */
  counts?: { digital: number; analog: number; total: number };
}

/**
 * The page's signature element, built like the reference "Flottenampel": two proportional
 * segments, the active one framed with an inset ring. Sits inline in the header bar; the hint
 * on what is counted travels as the group's description and tooltip.
 */
export function ViewSwitch({ view, onChange, counts }: Props) {
  const { t, i18n } = useTranslation();
  const number = (value: number) => formatNumber(value, i18n.language);
  const percent = (value: number) => (counts && counts.total > 0 ? Math.round((value / counts.total) * 100) : 0);

  return (
    <Box
      role="group"
      aria-label={t("logbookAudit.viewSwitch.label")}
      aria-describedby="logbook-audit-view-hint"
      title={t("logbookAudit.viewSwitch.hint")}
      sx={{ display: "flex", height: 34, borderRadius: `${AUDIT_TOKENS.radius.control}px`, overflow: "hidden", gap: "2px", "@media (max-width: 820px)": { height: "auto" } }}
    >
      <Box component="span" id="logbook-audit-view-hint" sx={visuallyHidden}>
        {t("logbookAudit.viewSwitch.hint")}
      </Box>
      {AUDIT_VIEWS.map(candidate => {
        const count = counts?.[candidate];
        const share = counts && count !== undefined && counts.total > 0 ? count / counts.total : 0.5;
        const label = t(`logbookAudit.viewSwitch.${candidate}`);
        return (
          <Segment
            key={candidate}
            view={candidate}
            active={view === candidate}
            aria-pressed={view === candidate}
            onClick={() => onChange(candidate)}
            title={
              count === undefined
                ? label
                : t("logbookAudit.viewSwitch.segmentTitle", { view: label, trips: number(count), percent: percent(count) })
            }
            style={{ flexGrow: Math.max(MIN_GROW, share) }}
          >
            {count === undefined ? `${label} · —` : `${label} · ${number(count)} · ${percent(count)} %`}
          </Segment>
        );
      })}
    </Box>
  );
}
